import { useEffect, useState } from 'react';
import { Grid3x3, Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { useTelephonyStore } from '../../store/telephonyStore';
import type { CallPhase } from '../../types/telephony';

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const PHASE_LABEL: Record<CallPhase, string> = {
  idle: '',
  connecting: 'Connexion…',
  ringing: 'Appel entrant…',
  active: 'En communication',
  ended: 'Terminé',
};

/**
 * Floating softphone. Mounting it registers the Twilio Device, so inbound calls
 * ring the web client whenever the cockpit is open (and the mobile in parallel).
 */
export function Softphone() {
  const { phase, callDirection, activeNumber, muted, error, endNotice, initDevice, hangup, acceptCall, rejectCall, toggleMute, sendDigits } =
    useTelephonyStore();
  const incomingRinging = phase === 'ringing' && callDirection === 'inbound';
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [typed, setTyped] = useState('');

  // A new call starts with the keypad closed and nothing typed.
  useEffect(() => {
    if (phase !== 'active') {
      setKeypadOpen(false);
      setTyped('');
    }
  }, [phase]);

  useEffect(() => {
    void initDevice();
  }, [initDevice]);

  if (phase === 'idle' && !error) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{PHASE_LABEL[phase]}</span>
        <span className={`h-2 w-2 rounded-full ${phase === 'active' ? 'bg-green-500' : 'bg-amber-500'}`} />
      </div>
      <p className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
        {activeNumber ?? 'AutoQuote'}
      </p>
      {phase === 'ended' && endNotice && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{endNotice}</p>}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {keypadOpen && (
        <div className="mt-3">
          <p className="h-5 truncate text-center font-mono text-sm text-gray-600 dark:text-gray-300">{typed}</p>
          <div className="mt-1 grid grid-cols-3 gap-1">
            {DTMF_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  sendDigits(k);
                  setTyped((t) => t + k);
                }}
                className="h-9 rounded-md bg-gray-50 text-base font-medium text-gray-900 hover:bg-gray-100 active:bg-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      )}
      {incomingRinging ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={acceptCall}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-green-600 py-2 text-sm font-medium text-white hover:bg-green-700"
          >
            <Phone className="h-4 w-4" />
            Répondre
          </button>
          <button
            type="button"
            onClick={rejectCall}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <PhoneOff className="h-4 w-4" />
            Refuser
          </button>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={toggleMute}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gray-100 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"
          >
            {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {muted ? 'Activer' : 'Muet'}
          </button>
          {phase === 'active' && (
            <button
              type="button"
              onClick={() => setKeypadOpen((o) => !o)}
              aria-pressed={keypadOpen}
              title="Clavier (taper un choix sur un standard)"
              className={`flex items-center justify-center rounded-lg px-3 py-2 ${
                keypadOpen
                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200'
              }`}
            >
              <Grid3x3 className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={hangup}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <PhoneOff className="h-4 w-4" />
            Raccrocher
          </button>
        </div>
      )}
    </div>
  );
}
